import React, { useState, useEffect, useRef } from 'react';
import { Volume2, CheckCircle, XCircle, Star, ArrowRight } from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { getRandomWords } from '../../lib/vocabulary';
import { speechService } from '../../services/speechService';
import { VocabularyWord } from '../../types';

const WORDS_PER_GAME = 8;
const POINTS_PER_WORD = 15;

export function SpellItGame() {
  const { gameState, addScore, markWordLearned, showCelebration, endGame } = useApp();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const currentSession = gameState.currentSession;

  // Initialize game
  useEffect(() => {
    if (currentSession) {
      const gameWords = getRandomWords(
        WORDS_PER_GAME,
        currentSession.level,
        currentSession.language
      );
      setWords(gameWords);
      setCurrentWordIndex(0);
      setScore(0);
      setCorrectCount(0);
    }
  }, [currentSession]);

  // Focus input when word changes
  useEffect(() => {
    if (words.length > 0 && currentWordIndex < words.length) {
      // Note: Auto-play removed - browsers block speech synthesis unless triggered by user interaction
      // Users must click the "Play Word" button to hear the word

      // Focus input
      inputRef.current?.focus();
    }
  }, [currentWordIndex, words]);

  const playWord = () => {
    if (words.length === 0 || currentWordIndex >= words.length || !currentSession) return;

    const currentWord = words[currentWordIndex];
    setIsSpeaking(true);

    speechService.speak(currentWord.word, currentSession.language, {
      rate: 0.7, // Slower for spelling
      onEnd: () => setIsSpeaking(false),
      onStart: () => setIsSpeaking(true),
    });
  };

  const handleCheck = () => {
    if (!userInput.trim() || isChecked) return;

    const currentWord = words[currentWordIndex];
    const correct =
      userInput.trim().toLowerCase() === currentWord.word.toLowerCase();
    setIsCorrect(correct);
    setIsChecked(true);

    if (correct) {
      const points = POINTS_PER_WORD;
      setScore(score + points);
      setCorrectCount(correctCount + 1);
      addScore(points);
      markWordLearned(currentWord.id);

      // Auto-advance after 1.5s on success
      setTimeout(() => {
        handleNext();
      }, 1500);
    }
  };

  const handleNext = () => {
    if (currentWordIndex + 1 >= words.length) {
      // Game complete
      handleGameComplete();
    } else {
      setCurrentWordIndex(currentWordIndex + 1);
      setUserInput('');
      setIsChecked(false);
      setIsCorrect(null);
      setShowHint(false);
    }
  };

  const handleSkip = () => {
    setIsChecked(true);
    setIsCorrect(false);

    setTimeout(() => {
      handleNext();
    }, 1000);
  };

  const handleGameComplete = () => {
    showCelebration();

    setTimeout(() => {
      endGame();
    }, 3000);
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!isChecked) {
        handleCheck();
      } else if (isCorrect) {
        handleNext();
      }
    }
  };

  if (!currentSession || words.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading game...</div>
      </div>
    );
  }

  const currentWord = words[currentWordIndex];
  const progress = ((currentWordIndex + 1) / words.length) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold text-primary flex items-center gap-2">
              🏆 Spell It!
            </h2>
            <p className="text-gray-600 mt-1">Type what you hear!</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <Star className="text-yellow-500 fill-yellow-500" />
              <span className="text-3xl font-bold text-yellow-600">{score}</span>
            </div>
            <p className="text-sm text-gray-500">Score</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-sm text-gray-600 mt-2 text-center">
          Word {currentWordIndex + 1} of {words.length}
        </div>
      </div>

      {/* Main Game Area */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-6">
        {/* Sound Button */}
        <div className="text-center mb-8">
          <button
            onClick={playWord}
            disabled={isSpeaking}
            className={`btn-primary text-xl px-12 py-6 flex items-center gap-3 mx-auto ${
              isSpeaking ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            <Volume2 className={isSpeaking ? 'animate-pulse' : ''} size={32} />
            {isSpeaking ? 'Playing...' : 'Play Word'}
          </button>

          {/* Hint button */}
          <button
            onClick={toggleHint}
            className="mt-4 text-sm text-blue-600 hover:text-blue-700 underline"
          >
            {showHint ? 'Hide Hint' : 'Show Hint'}
          </button>

          {showHint && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg animate-fadeIn">
              <p className="text-sm text-gray-600">
                The word has <strong>{currentWord.word.length}</strong> letters
                {currentWord.category && (
                  <>
                    {' '}
                    and is a <strong>{currentWord.category}</strong> word
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="max-w-md mx-auto">
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isChecked}
            placeholder="Type the word here..."
            className="w-full px-6 py-4 text-2xl text-center border-4 border-gray-300 rounded-2xl focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />

          {/* Feedback */}
          {isChecked && (
            <div
              className={`mt-6 animate-fadeIn text-center p-6 rounded-2xl ${
                isCorrect
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2 text-xl font-bold mb-2">
                {isCorrect ? (
                  <>
                    <CheckCircle size={28} />
                    Perfect! +{POINTS_PER_WORD} points
                  </>
                ) : (
                  <>
                    <XCircle size={28} />
                    Not quite right!
                  </>
                )}
              </div>
              {!isCorrect && (
                <div className="text-lg mt-2">
                  The correct spelling is: <strong>{currentWord.word}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center mt-8">
          {!isChecked ? (
            <>
              <button
                onClick={handleCheck}
                disabled={!userInput.trim()}
                className="btn-primary flex items-center gap-2"
              >
                <CheckCircle size={20} />
                Check Answer
              </button>
              <button onClick={handleSkip} className="btn-secondary">
                Skip
              </button>
            </>
          ) : (
            !isCorrect && (
              <button onClick={handleNext} className="btn-primary flex items-center gap-2">
                Next Word
                <ArrowRight size={20} />
              </button>
            )
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-green-600">{correctCount}</div>
            <div className="text-sm text-gray-600">Correct</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-red-600">
              {currentWordIndex - correctCount}
            </div>
            <div className="text-sm text-gray-600">Wrong</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-600">
              {currentWordIndex > 0
                ? Math.round((correctCount / currentWordIndex) * 100)
                : 0}
              %
            </div>
            <div className="text-sm text-gray-600">Accuracy</div>
          </div>
        </div>
      </div>
    </div>
  );
}
